import React, { Suspense } from "react"

import { ApolloClientProvider } from "./graphql"

import CssBaseline from "@mui/material/CssBaseline"
import { ThemeProvider } from "@mui/material/styles"
import StyledEngineProvider from "@mui/styled-engine/StyledEngineProvider"

import "./app.css"
import "react-toastify/dist/ReactToastify.css"
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { RouterProvider, createBrowserRouter } from "react-router-dom"
import { ToastContainer } from "react-toastify"

import FullPageLoading from "@/components/fullpage_loading"
import { FeatureFlagsProvider } from "@/contexts/feature_flags_context"
import { LocaleProvider, useLocale } from "@/contexts/locale"
import type { GetCurrentUserQuery } from "@/graphql/graphql"
import { useHydrate } from "@/store"

import { routes } from "./routes"
import theme from "./theme"

const router = createBrowserRouter(routes)

interface AppProps {
  currentUser: GetCurrentUserQuery["currentUser"] | null
}

const App: React.FC<AppProps> = ({ currentUser }) => {
  useHydrate({ currentUser })

  return (
    <LocaleProvider>
      <AppContent currentUser={currentUser} />
    </LocaleProvider>
  )
}

const AppContent: React.FC<AppProps> = ({ currentUser }) => {
  const { locale } = useLocale()

  return (
    <ApolloClientProvider currentUser={currentUser}>
      <FeatureFlagsProvider>
        <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={locale}>
          <ThemeProvider theme={theme}>
            <StyledEngineProvider injectFirst>
              <CssBaseline />
              <Suspense fallback={<FullPageLoading />}>
                <RouterProvider router={router} />
              </Suspense>
              <ToastContainer
                position="top-right"
                autoClose={4000}
                hideProgressBar
                closeOnClick
                pauseOnFocusLoss
                draggable={false}
                className="tabb-toast-container"
              />
            </StyledEngineProvider>
          </ThemeProvider>
        </LocalizationProvider>
      </FeatureFlagsProvider>
    </ApolloClientProvider>
  )
}

export default App
